import { Action, Dispatch } from 'redux'
import { db, FirebaseWriteBatch } from 'firebase/index'
import { push } from 'connected-react-router'
import { ConfirmStock, StockType, StockUnionType } from 'types/stock'
import { CheckoutItemType } from 'types/checkoutItem'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { initConfirmAction } from 'reducks/confirm/actions'
import { BreadType } from 'types/bread'
import { State } from 'index'
import { sendNewStoreMessage } from 'reducks/messages/operations'
import { publishFirstStock } from 'repos/store'
import { updatePublishFirstStockAction } from 'reducks/store/actions'
import { addNotifiedTypes } from 'reducks/bread/operations'
import { addStoreStockMessageQueue } from 'reducks/storeStockMessageQueue/operations'

export const saveCollectiveStocks = (
  breads: BreadType[],
  confirmStocks: ConfirmStock[],
  checkoutItems: CheckoutItemType[],
  type: StockUnionType
) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    const storeRef = getState().auth.store_ref
    const storeStatus = getState().store.status
    const isBeforePublishingStock = getState().store.is_before_publishing_stock
    const firstStocks: StockType[] = []
    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0
    dispatch(showLoadingAction())

    for (let i = 0; i < confirmStocks.length; i = (i + 1) | 0) {
      const confirmStock = confirmStocks[i]
      const { addedStocks, deletedStocks, updatedStocks } = confirmStock

      for (let j = 0; j < addedStocks.length; j = (j + 1) | 0) {
        const addedStock = addedStocks[j]
        const ref = addedStock.stock_ref
        if (!ref) continue
        delete addedStock.stock_ref
        batchArray[batchIndex].set(ref, addedStock)
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }

      addedStocks
        .reduce((prev: StockType[], stock) => {
          if (prev.some((prevStock) => prevStock.bread_ref.id === stock.bread_ref.id)) return prev
          return [...prev, stock]
        }, [])
        .forEach((stock) => {
          const bread = breads.find((bread) => bread.bread_ref?.id === stock.bread_ref.id)
          if (!bread) return
          if (!bread.notified_types?.includes(type)) {
            firstStocks.push(stock)
          }
        })

      for (let j = 0; j < deletedStocks.length; j = (j + 1) | 0) {
        const deletedStock = deletedStocks[j]
        if (!deletedStock.stock_ref) continue
        // 注文のある日付は削除せずに在庫数を0にする
        const isOrdered = checkoutItems.some(
          (item) => item.date === deletedStock.date && item.bread_ref.id === deletedStock.bread_ref.id
        )
        if (isOrdered) {
          const ref = deletedStock.stock_ref
          delete deletedStock.stock_ref
          batchArray[batchIndex].set(ref, { stock: 0 }, { merge: true })
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
        } else {
          batchArray[batchIndex].delete(deletedStock.stock_ref)
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
        }
      }

      for (let j = 0; j < updatedStocks.length; j = (j + 1) | 0) {
        const updatedStock = updatedStocks[j]
        const ref = updatedStock.stock_ref
        if (!ref) continue
        delete updatedStock.stock_ref
        batchArray[batchIndex].set(ref, updatedStock, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }
    }
    // 店舗が初めて在庫を公開する場合、5km圏内のユーザーにPush通知を送る
    if (isBeforePublishingStock && storeStatus === 'public') {
      await publishFirstStock(storeRef)
      dispatch(updatePublishFirstStockAction())
      await sendNewStoreMessage(storeRef)
    } else {
      // 初めて在庫を設定する商品が存在する場合、ユーザーに通知を送る
      const promises: Array<Promise<void>> = []
      firstStocks.forEach((firstStock) => {
        const bread = breads.find((bread) => bread.bread_ref?.id === firstStock.bread_ref.id)
        if (!bread || !bread.bread_ref) return
        promises.push(addNotifiedTypes({ bread, type }))
        promises.push(addStoreStockMessageQueue({ breadRef: bread.bread_ref, storeRef }))
      })
      await Promise.all(promises)
    }
    try {
      await batchArray.forEach(async (batch) => await batch.commit())
      const path = `/schedule/collective_setting/complete`
      dispatch(push(path))
      dispatch(initConfirmAction())
      dispatch(hideLoadingAction())
    } catch (e) {
      console.error(e)
      dispatch(hideLoadingAction())
    }
  }
}

export default saveCollectiveStocks
